import React from 'react'
import { GetServerSideProps } from 'next'
import { ContentfulProvider } from '@pcln/contentful-components'
import Layout from '@/components/Layout'
import type { CustomContext, ContentPageProps } from '@/types'
import VIP from '@/components/VIP'
import DEFAULT_META_TAGS from '@/constants/meta'
import Cobrand from '@/components/Cobrand'
import Meta from '@/components/Meta'
import useCanonicalUrl from '@/hooks/useCanonicalUrl'
import generateBootstrapData from '../utils/generate-bootstrap-data'
import fetchPCLNHeaderFooterContent from '../server/utils/pcln-header-footer'
import setiService from '../server/middleware/seti'
import mapSetiResponse from '../utils/map-seti-response'
import fetchSignInStatus from '../server/utils/signin-status'
import getCookies from '../server/utils/get-cookies'
import getXPclnHeaders from '../server/utils/get-x-pcln-headers'
import getContentfulData from '../server/utils/get-contentful-data'
import retrieveContentfulSecrets from '../server/utils/retrieve-contentful-secrets'

function Vip({ content, headerHTML, footerHTML }: ContentPageProps) {
  const canonicalUrl = useCanonicalUrl()
  return (
    <>
      <Meta
        description={DEFAULT_META_TAGS.description.vip}
        pageTitle={DEFAULT_META_TAGS.title.vip}
        canonicalUrl={canonicalUrl}
      />
      <ContentfulProvider contentful={content}>
        <Layout headerHTML={headerHTML} footerHTML={footerHTML}>
          <Cobrand />
          <VIP />
        </Layout>
      </ContentfulProvider>
    </>
  )
}

export const getServerSideProps: GetServerSideProps<
  ContentPageProps
> = async ctx => {
  const { req, res } = ctx as unknown as CustomContext
  const pricelineSpecificHeaders = getXPclnHeaders(req.headers)

  const bootstrapData = generateBootstrapData('vip', req, res)
  const { webstats, referral, appVersion } = bootstrapData
  const { clientGUID: cguid } = webstats
  const { contentfulDeliverySecret, contentfulPreviewSecret } =
    retrieveContentfulSecrets()

  const [pclnHeaderFooterContent, signInStatus, { content, experimentNames }] =
    await Promise.all([
      fetchPCLNHeaderFooterContent(req, res),
      fetchSignInStatus(
        cguid,
        getCookies(req.headers).dmc || '',
        pricelineSpecificHeaders
      ),
      getContentfulData({
        contentTypeId: 'pageLanding',
        contentfulDeliverySecret,
        contentfulPreviewSecret,
        product: 'Promotion',
        contentPreview: ctx.query['content-preview'] === 'true'
      })
    ])

  if (res) {
    if (signInStatus.signedIn) {
      res.writeHead(302, { Location: `/next-profile${req.originalUrl}` })
      res.end()
    }
  }
  const setiResponseEvents = await setiService(req, {
    appVersion,
    allowSetiOverrides: true,
    webstats,
    referral,
    reqQueryParams: ctx.query,
    ...(experimentNames && {
      additionalExperiments: experimentNames
    })
  })
  return {
    props: {
      content,
      ...pclnHeaderFooterContent,
      bootstrapData: {
        ...bootstrapData,
        fullSETI: setiResponseEvents,
        seti: mapSetiResponse(setiResponseEvents),
        signInStatus
      }
    }
  }
}

export default Vip
